import { useState, useEffect } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import { useHostProfile, useUpdateProfile, useChangePassword } from '../../hooks/useAccount';

const PLAN_COLORS: Record<string, 'default' | 'primary' | 'secondary'> = {
  FREE: 'default',
  PRO: 'primary',
  ENTERPRISE: 'secondary',
};

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(dateStr: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(dateStr));
}

export default function ProfilePage() {
  const { data: profile, isLoading, isError, error } = useHostProfile();
  const updateProfile = useUpdateProfile();
  const changePassword = useChangePassword();

  // Edit form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');

  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Populate form when profile loads
  useEffect(() => {
    if (profile) {
      setName(profile.name);
      setEmail(profile.email);
    }
  }, [profile]);

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileSuccess('');
    setProfileError('');

    try {
      await updateProfile.mutateAsync({ name, email });
      setProfileSuccess('Profile updated successfully');
    } catch (err) {
      setProfileError((err as Error).message || 'Failed to update profile');
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordSuccess('');
    setPasswordError('');

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    try {
      await changePassword.mutateAsync({ currentPassword, newPassword });
      setPasswordSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError((err as Error).message || 'Failed to change password');
    }
  };

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width="40%" height={48} />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2, borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2, borderRadius: 2 }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error">
        {(error as Error)?.message ?? 'Failed to load profile'}
      </Alert>
    );
  }

  if (!profile) return null;

  const initials = profile.name.charAt(0).toUpperCase();

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} mb={3}>
        My Profile
      </Typography>

      {/* Profile Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'center' }}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: 28 }}>
              {initials}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                <Typography variant="h5" fontWeight={600}>
                  {profile.name}
                </Typography>
                <Chip
                  label={profile.plan}
                  color={PLAN_COLORS[profile.plan] ?? 'default'}
                  size="small"
                />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {profile.email}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Member since {formatDate(profile.createdAt)}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Stats Summary */}
      <Grid container spacing={2} mb={3}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <EventIcon color="primary" />
              <Typography variant="h5" fontWeight={700}>
                {profile.stats.totalEvents}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Events
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <PeopleIcon color="primary" />
              <Typography variant="h5" fontWeight={700}>
                {profile.stats.totalGuests}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Guests
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <AttachMoneyIcon color="primary" />
              <Typography variant="h5" fontWeight={700}>
                {formatCents(profile.stats.totalRevenueCents)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Spent
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <EmailIcon color="primary" />
              <Typography variant="h5" fontWeight={700}>
                {profile.stats.totalEmailsSent}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Emails Sent
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Profile Form */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <PersonIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Edit Profile
            </Typography>
          </Stack>

          {profileSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setProfileSuccess('')}>
              {profileSuccess}
            </Alert>
          )}
          {profileError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setProfileError('')}>
              {profileError}
            </Alert>
          )}

          <form onSubmit={handleProfileSubmit}>
            <Stack spacing={2}>
              <TextField
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                required
                slotProps={{ htmlInput: { minLength: 1, maxLength: 100 } }}
              />
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                required
              />
              <Box>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={updateProfile.isPending}
                  sx={{ minHeight: 44 }}
                >
                  {updateProfile.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Stack>
          </form>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <LockIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Change Password
            </Typography>
          </Stack>

          {passwordSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPasswordSuccess('')}>
              {passwordSuccess}
            </Alert>
          )}
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPasswordError('')}>
              {passwordError}
            </Alert>
          )}

          <form onSubmit={handlePasswordSubmit}>
            <Stack spacing={2}>
              <TextField
                label="Current Password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                fullWidth
                required
              />
              <Divider />
              <TextField
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
                required
                slotProps={{ htmlInput: { minLength: 8, maxLength: 128 } }}
                helperText="At least 8 characters"
              />
              <TextField
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                fullWidth
                required
              />
              <Box>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={changePassword.isPending}
                  sx={{ minHeight: 44 }}
                >
                  {changePassword.isPending ? 'Changing...' : 'Change Password'}
                </Button>
              </Box>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
